import React, { useState } from 'react';
import {
  Alert,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
} from '@mui/material';
import { useChallengesTeamRankingsQuery } from 'generated/graphql';
import { TablePaginationActionsV5 } from 'retired/TablePaginationActions';
import { purpleLighten20, white } from 'utils/color-styles';
import { Challenge } from 'generated/graphql';

interface TeamsListProps {
  challenge: Challenge;
}

export const TeamsList = ({ challenge }: TeamsListProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data, error, loading } = useChallengesTeamRankingsQuery({
    variables: {
      where: { id: { equals: challenge.id } },
      skip: 0,
      take: 1,
      teamRankingsSkip: page * rowsPerPage,
      teamRankingsTake: rowsPerPage,
    },
    skip: !challenge.id,
  });

  const challengeData = data?.challenges?.items?.[0];
  const actionsCompletedRankings = challengeData?.teamRankings?.actionsCompleted;
  const totalTeams = challenge.generalStats?.totalTeams || 0;

  const goToPage = (__: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">Error loading teams: {error.message}</Alert>
      </Box>
    );
  }

  if (!actionsCompletedRankings || actionsCompletedRankings.length === 0) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="textSecondary">No team data available.</Typography>
      </Box>
    );
  }

  const showActionsCompleted = !!actionsCompletedRankings?.[0];

  return (
    <Box sx={{ mt: 4 }}>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader aria-label="team rankings table" className="colored purpleLighten20">
            <TableHead>
              <TableRow>
                <TableCell colSpan={showActionsCompleted ? 3 : 2} style={{ padding: 0, border: 0 }}>
                  <div
                    style={{
                      background: purpleLighten20,
                      width: '100%',
                      padding: '16px 16px 6px 16px',
                      color: white,
                      fontSize: '17px',
                    }}
                  >
                    Teams
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ background: purpleLighten20, color: white }}>
                  <Typography variant="subtitle2" style={{ color: white }}>
                    Team Name
                  </Typography>
                </TableCell>
                <TableCell style={{ background: purpleLighten20, color: white }}>
                  <Typography variant="subtitle2" style={{ color: white }}>
                    Rank
                  </Typography>
                </TableCell>
                {showActionsCompleted && (
                  <TableCell align="right" style={{ background: purpleLighten20, color: white }}>
                    <Typography variant="subtitle2" style={{ color: white }}>
                      Actions Completed
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {actionsCompletedRankings.map((actionsCompletedRanking) => (
                <TableRow key={actionsCompletedRanking.team.id}>
                  <TableCell>
                    <Typography variant="body2">{actionsCompletedRanking.team.title}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{actionsCompletedRanking.rank}</Typography>
                  </TableCell>
                  {showActionsCompleted && (
                    <TableCell align="right">
                      <Typography variant="body2">{actionsCompletedRanking.totalCount}</Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          count={totalTeams}
          page={page}
          onPageChange={goToPage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          ActionsComponent={TablePaginationActionsV5}
        />
      </Paper>
    </Box>
  );
};
